import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import BrandMark from "../ui/BrandMark";
import NetworkBackground from "../effects/NetworkBackground";
import Reveal, { EASE } from "../ui/Reveal";
import DirArrow from "../ui/DirArrow";
import useLang from "../../hooks/useLang";

// Swaps the bold word in the hero headline with a short vertical slide.
const WordRotator = ({ words }) => {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion || words.length < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % words.length), 2800);
    return () => clearInterval(id);
  }, [reduceMotion, words.length]);

  return (
    <span className="relative inline-flex overflow-hidden pb-[0.12em] align-bottom">
      <span key={words[index]} className={`inline-block whitespace-nowrap ${index === 0 ? "" : "animate-word-in"}`}>
        {words[index]}
      </span>
    </span>
  );
};

// Each headline line rises out of its own mask on first load.
const MaskLine = ({ delay = 0, children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// The hero: rotating headline word, intro and the animated brand mark.
const HeroSection = () => {
  const { t } = useLang();

  return (
    <section className="relative bg-ink text-white overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute -top-40 end-[-10%] h-[620px] w-[620px] rounded-full bg-rose/10 blur-[120px]" />
      <NetworkBackground />
      <div className="relative container-site min-h-[calc(100vh-72px)] py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-rose-light"
          >
            {t("hero.badge")}
          </motion.span>

          <h1 className="display mt-6">
            <MaskLine delay={0.1}>
              <span className="font-light">{t("hero.line1")}</span>
            </MaskLine>
            <MaskLine delay={0.22}>
              <span className="font-extrabold">
                <WordRotator words={t("hero.words", { returnObjects: true })} />
              </span>
            </MaskLine>
            <MaskLine delay={0.34}>
              <span className="block mt-3 font-light text-white/55 text-[clamp(1.6rem,3vw,2.6rem)] leading-[1.15] tracking-[-0.02em]">
                {t("hero.line3")}
              </span>
            </MaskLine>
          </h1>

          <Reveal delay={0.45} y={16}>
            <p className="lead text-white/60 max-w-xl mt-8">{t("hero.description")}</p>
          </Reveal>

          <Reveal delay={0.55} y={16} className="flex flex-wrap items-center gap-6 mt-10">
            <Link to="/contact" className="btn-rose group">
              {t("hero.cta1")} <DirArrow nudge size={15} />
            </Link>
            <a href="#what-we-do" className="link-arrow group text-white hover:text-rose-light">
              {t("hero.cta2")} <DirArrow nudge size={15} />
            </a>
          </Reveal>

        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <BrandMark variant="onDark" animated className="w-64 sm:w-80 lg:w-full max-w-[460px] h-auto" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
